import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, User, X, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const LiveChat = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [conversationStatus, setConversationStatus] = useState('open');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load messages on mount
  useEffect(() => {
    loadMessages();
    
    // Start polling for new messages every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      loadMessages();
    }, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found when loading messages');
        setLoading(false);
        return;
      }

      // Use relative URL - works on any domain
      console.log('Loading messages from:', '/api/messages/my-messages');
      
      const response = await fetch('/api/messages/my-messages', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Load messages response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Messages loaded:', data.length);
        setMessages(data);
      } else if (response.status === 401) {
        console.error('Unauthorized - token may be invalid');
        toast({
          title: "Session Expired",
          description: "Please log in again",
          variant: "destructive"
        });
        navigate('/login');
      } else {
        console.error('Failed to load messages:', response.status);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB",
          variant: "destructive"
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if (!message.trim() && !selectedFile) {
      console.log('Cannot send: empty message and no file');
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found - user not authenticated');
      toast({
        title: "Authentication Required",
        description: "Please log in to send messages",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    console.log('Sending message...', { 
      messageLength: message.trim().length, 
      hasFile: !!selectedFile 
    });

    setSending(true);
    try {
      // Use relative URL - works on any domain
      const formData = new FormData();
      formData.append('message_text', message.trim() || '(File attachment)');
      if (selectedFile) {
        formData.append('attachment', selectedFile);
      }
      
      console.log('Making request to:', '/api/messages/send');
      console.log('With authorization token:', token.substring(0, 20) + '...');
      
      const response = await fetch('/api/messages/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const newMessage = await response.json();
        console.log('Message sent successfully:', newMessage);
        setMessages(prev => [...prev, newMessage]);
        setMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        toast({
          title: "Message sent",
          description: "Support will respond shortly"
        });
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to send message:', response.status, errorData);
        throw new Error(errorData.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-cyan-950 to-blue-900 flex items-center justify-center">
        <div className="text-cyan-400 text-lg">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-cyan-950 to-blue-900 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-blue-900 to-cyan-900 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 sm:gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-semibold text-sm sm:text-base">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-cyan-400 text-xs sm:text-sm">Support Team</div>
              <div className="text-xs text-green-400 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Online
              </div>
            </div>
          </div>
          <div className="w-12 sm:w-16"></div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-cyan-600 mt-8">
            <p className="text-sm sm:text-base">No messages yet</p>
            <p className="text-xs sm:text-sm mt-2">Start a conversation with our support team</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] sm:max-w-[75%] ${
                msg.sender_type === 'user'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600'
                  : 'bg-slate-800/70 border border-cyan-500/20'
              } rounded-2xl px-3 sm:px-4 py-2 sm:py-3`}>
                <p className={`text-[10px] sm:text-xs font-semibold mb-1 ${
                  msg.sender_type === 'user' ? 'text-cyan-100' : 'text-cyan-400'
                }`}>
                  {msg.sender_name}
                </p>
                <p className={`text-xs sm:text-sm ${
                  msg.sender_type === 'user' ? 'text-white' : 'text-cyan-300'
                }`}>
                  {msg.message_text}
                </p>
                {msg.attachment_url && (
                  <a
                    href={'' + msg.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs sm:text-sm mt-2 flex items-center gap-1 underline ${
                      msg.sender_type === 'user' ? 'text-cyan-100' : 'text-cyan-400'
                    }`}
                  >
                    <Paperclip className="w-3 h-3" />
                    {msg.attachment_name}
                  </a>
                )}
                <p className={`text-[10px] sm:text-xs mt-1 ${
                  msg.sender_type === 'user' ? 'text-cyan-100' : 'text-cyan-700'
                }`}>
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* File Preview */}
      {selectedFile && (
        <div className="bg-slate-800/50 border-t border-cyan-500/20 px-3 sm:px-4 py-2">
          <div className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Paperclip className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <span className="text-xs sm:text-sm text-cyan-300 truncate">{selectedFile.name}</span>
              <span className="text-xs text-cyan-600 flex-shrink-0">
                ({(selectedFile.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              onClick={handleRemoveFile}
              className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-slate-900/95 border-t border-cyan-500/20 p-3 sm:p-4 pb-4 sm:pb-4">
        <div className="flex items-center gap-1 sm:gap-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.zip"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            className="text-cyan-400 hover:bg-cyan-500/10 w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0"
            disabled={sending}
          >
            <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <Input
            type="text"
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !sending && handleSend()}
            disabled={sending}
            className="flex-1 h-10 sm:h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 text-sm sm:text-base"
          />
          <Button
            type="button"
            onClick={handleSend}
            disabled={sending || (!message.trim() && !selectedFile)}
            className="h-10 sm:h-12 px-4 sm:px-6 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white neon-button flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
            ) : (
              <Send className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LiveChat;